import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, FileText, ExternalLink, Upload, CheckCircle } from 'lucide-react';
import { UserInfo } from '../../../types/verification';
import toast from 'react-hot-toast';
import { useLanguage } from '../../../contexts/LanguageContext';
import { uploadFile } from '../../../utils/firebase';

type Props = {
  onNext: (contractUrl?: string) => void;
  onBack: () => void;
  userType: 'guide' | 'organizer';
  userInfo: UserInfo;
  onContractUpload?: (signedContractUrl: string) => void;
};

type WebhookResponse = {
  status: string;
  message: string;
 download_url: string;
  view_url: string;
};

type ContractLanguage = 'ar' | 'en';

// Static contract URLs for different languages
const CONTRACT_URLS = {
  en: '/contracts/english-agreement.pdf',
  ar: '/contracts/arabic-agreement.pdf'
};

export const ContractStep = ({
  onNext,
  onBack,
  userType,
  userInfo,
  onContractUpload
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [contractLanguage, setContractLanguage] = useState<ContractLanguage>('en');
  const [contractUrls, setContractUrls] = useState<{
    downloadUrl: string;
    viewUrl: string;
  } | null>(null);
  const [signedContract, setSignedContract] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedContractUrl, setUploadedContractUrl] = useState<string | null>(null);

  const { translations } = useLanguage();

 const handleGenerateContract = async () => {
    setIsLoading(true);
    try {
      const data = {
        userType,
        userInfo,
        contractLanguage,
        date: new Date().toISOString()
      };

      // Fixed URL: removed extra spaces
      const webhookUrl = 'https://hook.eu2.make.com/ij4o3xwbnjqwtses4r4ixusfbrak2vko';

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      // Log status
      console.log('Response status:', response.status);

      if (!response.ok) {
        throw new Error(
          translations?.contractStep?.errors?.contractGeneration ||
            'Failed to generate contract'
        );
      }

      // ✅ Safely read response based on Content-Type
      const contentType = response.headers.get('content-type');
      let result: WebhookResponse;

      if (contentType && contentType.includes('application/json')) {
        // Parse JSON if content-type is JSON
        result = await response.json();
      } else {
        // Otherwise, read as text and fallback to static URLs
        const text = await response.text();
        console.log('Non-JSON response received:', text.trim());

        // Assume success if 200 OK
        result = {
          status: 'success',
          message: 'Contract generated',
          download_url: CONTRACT_URLS[contractLanguage],
          view_url: CONTRACT_URLS[contractLanguage],
        };
      }

      // Validate result
      if (result.status !== 'success') {
        throw new Error(
          result.message ||
          translations?.contractStep?.errors?.contractGeneration ||
          'Contract generation failed'
        );
      }

      if (!result.download_url || !result.view_url) {
        throw new Error('Missing download or view URL in response');
      }

      // Store the URLs
      setContractUrls({
        downloadUrl: result.download_url,
        viewUrl: result.view_url,
      });

      toast.success(
        translations?.contractStep?.success?.contractGenerated ||
          'Contract generated successfully'
      );
    } catch (error: any) {
      console.error('Error generating contract:', error);
      toast.error(
        translations?.contractStep?.errors?.contractGeneration ||
          'Failed to generate contract. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <h2 className="text-2xl font-bold">
        {translations?.contractStep?.headers?.contractReview || 'Contract Review'}
      </h2>

      {/* Static Contract Link */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden border">
        {/* Download Contract Links */}
        <div className="p-6 border-b bg-gray-50">
          <h3 className="font-semibold text-lg mb-4">
            {translations?.contractStep?.headers?.contractName || 'Download Contract'}
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            {translations?.contractStep?.subHeaders?.reviewMessage ||
              'Please download, sign, and upload the contract'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={CONTRACT_URLS.en}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Download size={20} />
              <span>Download English Contract</span>
            </a>
            <a
              href={CONTRACT_URLS.ar}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Download size={20} />
              <span>تحميل العقد باللغة العربية</span>
            </a>
          </div>
        </div>

        {/* Upload Signed Contract */}
        <div className="p-6 border-b">
          <h3 className="font-semibold text-lg mb-4">
            {translations?.contractStep?.headers?.contractName || 'Upload Signed Contract'}
          </h3>
          <p className="text-gray-60 text-sm mb-4">
            {translations?.contractStep?.subHeaders?.reviewMessage ||
              'Please upload the signed contract after downloading and signing it'}
          </p>
          
          {uploadedContractUrl ? (
            <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
              <CheckCircle size={20} />
              <span>
                {translations?.contractStep?.success?.contractGenerated || 'Contract uploaded successfully'}
              </span>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer border-gray-300 hover:border-primary-300 bg-gray-50 hover:bg-gray-100">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 mb-4 text-gray-500" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">
                        {translations?.contractStep?.buttons?.viewContract || 'Click to upload'}
                      </span>{' '}
                      {translations?.contractStep?.subHeaders?.reviewMessage || 'or drag and drop'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {'PDF only (MAX. 10MB)'}
                    </p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setSignedContract(e.target.files[0]);
                      }
                    }}
                  />
                </label>
              </div>
              
              {signedContract && (
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="text-blue-500" size={20} />
                    <span className="text-sm font-medium">{signedContract.name}</span>
                  </div>
                  <button
                    onClick={() => setSignedContract(null)}
                    className="text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              )}
              
              <button
                onClick={async () => {
                  if (!signedContract) return;
                  
                  setIsUploading(true);
                  try {
                    const result = await uploadFile(signedContract);
                    setUploadedContractUrl(result.url);
                    onContractUpload && onContractUpload(result.url);
                    toast.success(
                      'Contract uploaded successfully'
                    );
                  } catch (error) {
                    console.error('Error uploading contract:', error);
                    toast.error(
                      'Failed to upload contract'
                    );
                  } finally {
                    setIsUploading(false);
                  }
                }}
                disabled={!signedContract || isUploading}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white transition-colors ${
                  !signedContract || isUploading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary-500 hover:bg-primary-600'
                }`}
              >
                {isUploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>
                      'Uploading...'
                    </span>
                  </>
                ) : (
                  <>
                    <Upload size={20} />
                    <span>
                      'Upload Contract'
                    </span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Contract Actions */}
        <div className="p-6 space-y-6">
          {/* Generate Contract Button */}
          {!contractUrls && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGenerateContract}
              disabled={isLoading}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-white transition-colors ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-500 hover:bg-primary-600'
              }`}
            >
              <FileText size={20} />
              <span>
                {isLoading
                  ? translations?.contractStep?.buttons?.generatingContract ||
                    'Generating Contract...'
                  : translations?.contractStep?.buttons?.generateContract || 'Generate Contract'}
              </span>
            </motion.button>
          )}

          {/* Contract Actions */}
          {contractUrls && (
            <div className="space-y-4">
           {
            false &&    <div className="flex flex-col sm:flex-row gap-4">
                {/* Download Button */}
                <motion.a
                  href={contractUrls.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  <Download size={20} />
                  <span>
                    {translations?.contractStep?.buttons?.downloadContract ||
                      'Download Signed Contract'}
                  </span>
                </motion.a>

                {/* View Button */}
                <motion.a
                  href={contractUrls.viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <ExternalLink size={20} />
                  <span>
                    {translations?.contractStep?.buttons?.viewContract || 'View Signed Contract'}
                  </span>
                </motion.a>
              </div>
           }

              {/* Continue Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  onNext(contractUrls.viewUrl);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <span>
                  {userType === 'organizer'
                    ? translations?.contractStep?.buttons?.next || 'Next'
                    : translations?.contractStep?.buttons?.submit || 'Submit'}
                </span>
              </motion.button>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onBack}
          className="px-6 py-2 text-primary-500 border border-primary-500 rounded-lg hover:bg-primary-50"
        >
          {translations?.contractStep?.buttons?.back || 'Back'}
        </motion.button>
      </div>
    </motion.div>
  );
};